import { HeroStyle2 } from "~/components/HeroStyle2";
import { HoverRevealImages } from "~/components/HoverRevealImages";

import {
  Locale,
  type LocalizedStrings,
  useLocalization,
} from "~/components/localization/LocalizationProvider";
import { Box, Flex, Heading, Text } from "@radix-ui/themes";
import styles from "./meet-our-doctors-page.module.css";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import { Link } from "~/components/RouterLink";
import { OptimizedImage } from "~/components/OptimizedImage";

type Localization = {
  title: string;
  meetTheDreamTeamHeading: string;
  meetTheDreamTeamContent: string;
  meetDrGarcia: string;
  meetDrGarciaContentP1: string;
  meetDrGarciaContentP2: string;
  meetDrGarciaContentP3: string;
  meetDrGarciaContentP4: string;
  educationAndExperience: string;
  educationAndExperienceContentP1: string;
  educationAndExperienceContentP2: string;
  educationAndExperienceContentP3: string;
  educationAndExperienceContentListItem1: string;
  educationAndExperienceContentListItem2: string;
  educationAndExperienceContentListItem3: string;
  educationAndExperienceContentP4: string;
  communityInvolvement: string;
  communityInvolvementContentP1: string;
  communityInvolvementContentP2: string;
  communityInvolvementContentP3: string;
  communityInvolvementContentP4: string;
  smilingInMiami: string;
  smilingInMiamiContentP1: string;
  smilingInMiamiContentP2BeforeBraces: string;
  smilingInMiamiContentP2Braces: string;
  smilingInMiamiContentP2BetweenLinks: string;
  smilingInMiamiContentP2Invisalign: string;
  smilingInMiamiContentP2AfterInvisalign: string;
  smilingInMiamiContentP3: string;
  smilingInMiamiContentP4: string;
  professionalAffiliations: string;
  professionalAffiliationsContentListItem1: string;
  professionalAffiliationsContentListItem2: string;
  professionalAffiliationsContentListItem3: string;
  professionalAffiliationsContentListItem4: string;
  professionalAffiliationsContentListItem5: string;
  professionalAffiliationsContentListItem6: string;
  professionalAffiliationsContentListItem7: string;
  professionalAffiliationsContentListItem8: string;
  companioniName: string;
  companioniQuote: string;
  companioniBackgroundTitle: string;
  companioniBackgroundP1: string;
  companioniBackgroundP2: string;
  companioniBackgroundP3: string;
  companioniOutsideTitle: string;
  companioniOutsideP1: string;
};

const englishLocalization: Localization = {
  title: "Meet the Doctors",
  meetTheDreamTeamHeading: "Meet the Dream Team",
  meetTheDreamTeamContent:
    "Meet our doctors at Miami Orthodontic Specialists! Dr. Carmen Garcia-Paul and Dr. Chinchilla share a commitment to exceptional orthodontic care and a personalized, patient-centered approach to treatment.",
  meetDrGarcia: "Meet Dr. Carmen Garcia-Paul",
  meetDrGarciaContentP1:
    "Dr. Carmen Garcia-Paul was born and raised in Miami and—despite leaving the area for graduate studies—has always considered the area her home.",
  meetDrGarciaContentP2:
    "She graduated from Our Lady of Lourdes Academy, where she was both a scholar-athlete and valedictorian of her class. After Lourdes, Dr. Garcia-Paul was accepted into the honors program at the University of Miami.",
  meetDrGarciaContentP3:
    "While an undergraduate, Dr. Garcia-Paul began working for several local dentists and dental specialists in Miami. It was during this time that she first became interested in the specialty of orthodontics.",
  meetDrGarciaContentP4:
    "After seeing the positive effect that orthodontics could have on a person’s self-esteem, personality, and confidence, she decided to dedicate her life to helping others by perfecting their smiles.",
  educationAndExperience: "Education & Experience",
  educationAndExperienceContentP1:
    "After graduating from UM with honors, Dr. Garcia-Paul left her Miami home. Her graduate training first brought her to Philadelphia, where she attended the University of Pennsylvania School of Dental Medicine and earned her doctorate in dental medicine.",
  educationAndExperienceContentP2:
    "Following her graduation from Penn, Dr. Garcia-Paul was one of seven accepted into the competitive orthodontic residency program at the University of Michigan.",
  educationAndExperienceContentP3:
    "During her three years of specialty training, she won several scholarships and academic awards, including:",
  educationAndExperienceContentListItem1:
    "American Cleft Palate-Craniofacial Association Award",
  educationAndExperienceContentListItem2:
    "Colgate Palmolive/Hispanic Dental Association Scholarship",
  educationAndExperienceContentListItem3:
    "Rackham School of Graduate Studies Award",
  educationAndExperienceContentP4:
    "Staying current on state-of-the-art treatment methods is incredibly important to Dr. Garcia-Paul so she can provide you with the highest level of care. She attends continuing education courses and events throughout the year, so she is current on the latest techniques available.",
  communityInvolvement: "Community Involvement",
  communityInvolvementContentP1:
    "Throughout her educational journey, Dr. Garcia-Paul always found a way to give back to the community. She has volunteered with programs that offer free oral health screenings and dental services for children who could not afford treatment.",
  communityInvolvementContentP2:
    "In residency, she focused her master’s thesis on analyzing barriers to orthodontic treatment encountered by families of children with craniofacial anomalies.",
  communityInvolvementContentP3:
    "Since moving back to Miami, she volunteered on staff at Nicklaus Children’s Hospital Craniofacial Team and as an instructor in the dental assisting program at the Robert Morgan Technical Institute.",
  communityInvolvementContentP4:
    "She also became a provider for Smiles Change Lives, a community program that provides much-needed orthodontic care to children in Miami. Since starting with the program, she donated over $130,000 of free orthodontic treatment to children in need.",
  smilingInMiami: "Smiling In Miami",
  smilingInMiamiContentP1:
    "Since returning home almost a decade ago, Dr. Garcia-Paul has been able to transform hundreds of smiles by focusing on her dream of providing high-quality, personalized care.",
  smilingInMiamiContentP2BeforeBraces:
    "Her primary goal is to tailor her treatment with ",
  smilingInMiamiContentP2Braces: "braces",
  smilingInMiamiContentP2BetweenLinks: " or ",
  smilingInMiamiContentP2Invisalign: "Invisalign in Miami",
  smilingInMiamiContentP2AfterInvisalign:
    " to the individual needs of her patients.",
  smilingInMiamiContentP3:
    "Not only does her patient family continue to grow, but she is now the mother of three adventurous boys who keep her very busy outside the office.",
  smilingInMiamiContentP4:
    "In addition to her passion for creating beautiful smiles, Dr. Garcia-Paul also enjoys eating donuts, salsa dancing, watching super hero movies, and exploring new places with her husband and the boys.",
  professionalAffiliations: "Professional Affiliations",
  professionalAffiliationsContentListItem1: "American Board of Orthodontics",
  professionalAffiliationsContentListItem2:
    "American Association of Orthodontists",
  professionalAffiliationsContentListItem3:
    "Southern Association of Orthdontists",
  professionalAffiliationsContentListItem4:
    "Florida Association of Orthodontists",
  professionalAffiliationsContentListItem5: "American Dental Association",
  professionalAffiliationsContentListItem6: "Florida Dental Association",
  professionalAffiliationsContentListItem7: "Miami Dade Dental Society",
  professionalAffiliationsContentListItem8: "Smiles Change Lives",
  companioniName: "Meet Dr. Chinchilla",
  companioniQuote: "",
  companioniBackgroundTitle: "Background & Education",
  companioniBackgroundP1:
    "Dr. Chinchilla was born in Los Angeles, California, but raised in Miami, where he developed a deep connection to the community and a lifelong love for Miami sports, especially the University of Miami (Go Canes!).",
  companioniBackgroundP2:
    "He earned his dental degree from Nova Southeastern University, graduating with high honors, and then completed his Master’s in Orthodontics at St. Louis University. During his training, Dr. Chinchilla was awarded the Clinical Excellence Award, reflecting his true commitment to excellence in patient care. Dr. Chinchilla is passionate about creating healthy, confident smiles and strives to make every patient feel comfortable in a fun, welcoming environment. He takes pride in building lasting relationships with his patients and helping them feel confident throughout their orthodontic journey.",
  companioniBackgroundP3:
    "",
  companioniOutsideTitle: "Outside the Office",
  companioniOutsideP1:
    "Outside the office, he is a devoted husband and proud father of two energetic boys. He enjoys spending quality time with his family and perfecting his skills at the grill.",
};

const spanishLocalization: Localization = {
  title: "Conozca a los médicos",
  meetTheDreamTeamHeading: "Conoce al Dream Team",
  meetTheDreamTeamContent:
    "¡Conozca a nuestros médicos en Miami Orthodontic Specialists! La Dra. Carmen García-Paul y el Dr. Chinchilla comparten el compromiso de brindar una atención ortodóncica excepcional con un enfoque personalizado y centrado en el paciente.",
  meetDrGarcia: "Conozca a la Dra. Carmen García‑Paul",
  meetDrGarciaContentP1:
    "La Dra. Carmen García-Paul nació y creció en Miami y, a pesar de haber abandonado la zona para realizar estudios de posgrado, siempre ha considerado la zona como su hogar.",
  meetDrGarciaContentP2:
    "Se graduó de la Academia Nuestra Señora de Lourdes, donde fue una atleta académica y la mejor estudiante de su clase. Después de Lourdes, la Dra. García-Paul fue aceptada en el programa de honores de la Universidad de Miami.",
  meetDrGarciaContentP3:
    "Mientras era estudiante, la Dra. García‑Paul comenzó a trabajar para varios dentistas y especialistas dentales locales en Miami. Fue durante esta época cuando se interesó por primera vez en la especialidad de ortodoncia.",
  meetDrGarciaContentP4:
    "Después de ver el efecto positivo que la ortodoncia podía tener en la autoestima, la personalidad y la confianza de una persona, decidió dedicar su vida a ayudar a otros perfeccionando sus sonrisas.",
  educationAndExperience: "Educación y experiencia",
  educationAndExperienceContentP1:
    "Después de graduarse de la UM con honores, la Dra. García-Paul dejó su hogar en Miami. Su formación de posgrado la llevó primero a Filadelfia, donde asistió a la Facultad de Medicina Dental de la Universidad de Pensilvania y obtuvo su doctorado en medicina dental.",
  educationAndExperienceContentP2:
    "Después de graduarse en Penn, la Dra. García-Paul fue una de los siete aceptados en el competitivo programa de residencia en ortodoncia de la Universidad de Michigan.",
  educationAndExperienceContentP3:
    "Durante sus tres años de formación en la especialidad, obtuvo varias becas y premios académicos, entre ellos:",
  educationAndExperienceContentListItem1:
    "Premio de la Asociación Estadounidense de Paladar Hendido y Craneofacial",
  educationAndExperienceContentListItem2:
    "Beca Colgate Palmolive/Asociación Dental Hispana",
  educationAndExperienceContentListItem3:
    "Premio de la Escuela de Estudios de Posgrado Rackham",
  educationAndExperienceContentP4:
    "Para la Dra. García-Paul es sumamente importante mantenerse al día con los métodos de tratamiento más modernos para poder brindarle el más alto nivel de atención. Asiste a cursos y eventos de educación continua durante todo el año, por lo que está al día con las últimas técnicas disponibles.",
  communityInvolvement: "Participación comunitaria",
  communityInvolvementContentP1:
    "A lo largo de su trayectoria educativa, la Dra. García-Paul siempre encontró una manera de contribuir con la comunidad. Ha colaborado voluntariamente con programas que ofrecen exámenes de salud bucal gratuitos y servicios dentales para niños que no pueden costear el tratamiento.",
  communityInvolvementContentP2:
    "Durante su residencia, centró su tesis de maestría en analizar las barreras al tratamiento de ortodoncia que encuentran las familias de niños con anomalías craneofaciales.",
  communityInvolvementContentP3:
    "Desde que regresó a Miami, trabajó como voluntaria en el equipo craneofacial del Nicklaus Children's Hospital y como instructora en el programa de asistencia dental del Robert Morgan Technical Institute.",
  communityInvolvementContentP4:
    "También se convirtió en proveedora de Smiles Change Lives, un programa comunitario que brinda atención de ortodoncia muy necesaria a los niños de Miami. Desde que comenzó con el programa, donó más de $130,000 en tratamientos de ortodoncia gratuitos a niños necesitados.",
  smilingInMiami: "Sonriendo en Miami",
  smilingInMiamiContentP1:
    "Desde que regresó a casa hace casi una década, la Dra. García-Paul ha podido transformar cientos de sonrisas al centrarse en su sueño de brindar atención personalizada y de alta calidad.",
  smilingInMiamiContentP2BeforeBraces:
    "Su objetivo principal es adaptar su tratamiento con ",
  smilingInMiamiContentP2Braces: "brackets",
  smilingInMiamiContentP2BetweenLinks: " o ",
  smilingInMiamiContentP2Invisalign: "Invisalign en Miami",
  smilingInMiamiContentP2AfterInvisalign:
    " a las necesidades individuales de sus pacientes.",
  smilingInMiamiContentP3:
    "No sólo su paciente familia continúa creciendo, sino que ahora es madre de tres niños aventureros que la mantienen muy ocupada fuera de la oficina.",
  smilingInMiamiContentP4:
    "Además de su pasión por crear hermosas sonrisas, a la Dra. García-Paul también le gusta comer donas, bailar salsa, ver películas de superhéroes y explorar nuevos lugares con su esposo y sus hijos.",
  professionalAffiliations: "Afiliaciones profesionales",
  professionalAffiliationsContentListItem1: "Junta Americana de Ortodoncia",
  professionalAffiliationsContentListItem2:
    "Asociación Americana de Ortodoncistas",
  professionalAffiliationsContentListItem3:
    "Asociación Sureña de Ortodoncistas",
  professionalAffiliationsContentListItem4:
    "Asociación de Ortodoncistas de Florida",
  professionalAffiliationsContentListItem5: "Asociación Dental Americana",
  professionalAffiliationsContentListItem6: "Asociación Dental de Florida",
  professionalAffiliationsContentListItem7: "Sociedad Dental de Miami Dade",
  professionalAffiliationsContentListItem8: "Las sonrisas cambian vidas",
  companioniName: "Conozca al Dr. Chinchilla",
  companioniQuote: "",
  companioniBackgroundTitle: "Antecedentes y Educación",
  companioniBackgroundP1:
    "El Dr. Chinchilla nació en Los Ángeles, California, pero creció en Miami, donde desarrolló una conexión profunda con la comunidad y un amor de toda la vida por los deportes de Miami, especialmente la Universidad de Miami (Go Canes!).",
  companioniBackgroundP2:
    "Obtuvo su título dental en Nova Southeastern University, graduándose con altos honores, y luego completó su maestría en ortodoncia en St. Louis University. Durante su formación, el Dr. Chinchilla recibió el Clinical Excellence Award, reflejo de su compromiso con la excelencia en la atención al paciente. Le apasiona crear sonrisas saludables y seguras, y se esfuerza por hacer que cada paciente se sienta cómodo en un ambiente divertido y acogedor.",
  companioniBackgroundP3:
    "",
  companioniOutsideTitle: "Fuera de la Oficina",
  companioniOutsideP1:
    "Fuera de la oficina, es un esposo dedicado y orgulloso padre de dos niños llenos de energía. Disfruta pasar tiempo de calidad con su familia y perfeccionar sus habilidades en la parrilla.",
};

const localized: LocalizedStrings<Localization> = {
  [Locale.EN]: englishLocalization,
  [Locale.ES]: spanishLocalization,
};

export function MeetOurDoctorsPage() {
  const getLocalization = useLocalization(localized);
  return (
    <>
      <HeroStyle2
        heroImageUrl="/about-us-hero-2026.jpg"
        title={getLocalization("title")}
        backgroundPosition="50% 30%"
        marginTop="-25px"
      />
      <Flex
        className={`section ${styles.meetTheDreamTeamSection}`}
        width="100%"
        align="center"
        justify="center"
      >
        <Flex direction="column" gap="7" className={`innerSection`} p="5">
          <Heading as="h2" className={styles.heading} weight="bold">
            {getLocalization("meetTheDreamTeamHeading")}
          </Heading>
          <Text
            as="p"
            size="4"
            style={{
              lineHeight: "1.55em",
              letterSpacing: "normal",
              textWrap: "pretty",
            }}
          >
            {getLocalization("meetTheDreamTeamContent")}
          </Text>
        </Flex>
      </Flex>
      <Flex
        width="100%"
        justify="center"
        align="center"
        className={`section ${styles.meetTheDoctorContainerOuter}`}
      >
        <Box className={styles.meetTheDoctorContainer}>
          <Flex
            width="100%"
            justify="center"
            align="center"
            className={styles.drGarciaImage}
          >
            <HoverRevealImages
              topImage="/dr-garcia-paul-2026.jpg"
              bottomImage="/dr-garcia-paul-funny-2026.jpg"
              alt="Dr. Carmen Garcia-Paul"
              className={`${styles.doctorImage} ${styles.drGarciaPortrait}`}
              fill
            />
          </Flex>
          <Flex gap="3" direction="column" className={styles.meetDrGarcia}>
            <Heading as="h2" weight="bold" className={styles.subsectionHeading}>
              {getLocalization("meetDrGarcia")}
            </Heading>
            <Text as="p" size="4">
              {getLocalization("meetDrGarciaContentP1")}
            </Text>
            <Text as="p" size="4">
              {getLocalization("meetDrGarciaContentP2")}
            </Text>
            <Text as="p" size="4">
              {getLocalization("meetDrGarciaContentP3")}
            </Text>
            <Text as="p" size="4">
              {getLocalization("meetDrGarciaContentP4")}
            </Text>
          </Flex>
          <Flex
            gap="3"
            direction="column"
            className={styles.educationAndExperience}
          >
            <Heading as="h4" weight="bold" className={styles.heading3}>
              {getLocalization("educationAndExperience")}
            </Heading>
            <Text as="p" size="4">
              {getLocalization("educationAndExperienceContentP1")}
            </Text>
            <Text as="p" size="4">
              {getLocalization("educationAndExperienceContentP2")}
            </Text>
            <Text as="p" size="4">
              {getLocalization("educationAndExperienceContentP3")}
            </Text>
            <ul>
              <li>
                <Text size="3">
                  {getLocalization("educationAndExperienceContentListItem1")}
                </Text>
              </li>
              <li>
                <Text size="3">
                  {getLocalization("educationAndExperienceContentListItem2")}
                </Text>
              </li>
              <li>
                <Text size="3">
                  {getLocalization("educationAndExperienceContentListItem3")}
                </Text>
              </li>
            </ul>
            <Text as="p" size="4">
              {getLocalization("educationAndExperienceContentP4")}
            </Text>
          </Flex>
          <Flex
            gap="3"
            direction="column"
            className={styles.communityInvolvement}
          >
            <Heading as="h4" weight="bold" className={styles.heading3}>
              {getLocalization("communityInvolvement")}
            </Heading>
            <Text as="p" size="4">
              {getLocalization("communityInvolvementContentP1")}
            </Text>
            <Text as="p" size="4">
              {getLocalization("communityInvolvementContentP2")}
            </Text>
            <Text as="p" size="4">
              {getLocalization("communityInvolvementContentP3")}
            </Text>
            <Text as="p" size="4">
              {getLocalization("communityInvolvementContentP4")}
            </Text>
          </Flex>
          <Flex gap="3" direction="column" className={styles.smilingInMiami}>
            <Heading as="h4" weight="bold" className={styles.heading3}>
              {getLocalization("smilingInMiami")}
            </Heading>
            <Text as="p" size="4">
              {getLocalization("smilingInMiamiContentP1")}
            </Text>
            <Text as="p" size="4">
              {getLocalization("smilingInMiamiContentP2BeforeBraces")}
              <Link to="/miami-braces" className={styles.inlineLink}>
                {getLocalization("smilingInMiamiContentP2Braces")}
              </Link>
              {getLocalization("smilingInMiamiContentP2BetweenLinks")}
              <Link to="/invisalign-miami" className={styles.inlineLink}>
                {getLocalization("smilingInMiamiContentP2Invisalign")}
              </Link>
              {getLocalization("smilingInMiamiContentP2AfterInvisalign")}
            </Text>
            <Text as="p" size="4">
              {getLocalization("smilingInMiamiContentP3")}
            </Text>
            <Text as="p" size="4">
              {getLocalization("smilingInMiamiContentP4")}
            </Text>
          </Flex>
          <Flex
            gap="3"
            direction="column"
            className={styles.professionalAffiliations}
          >
            <OptimizedImage
              src="/carmen-family-2024.jpg"
              className={styles.familyImage}
              alt="Dr. Carmen Garcia-Paul and family"
            />
            <Heading as="h4" weight="bold" className={styles.heading3}>
              {getLocalization("professionalAffiliations")}
            </Heading>
            <ul>
              <li>
                <Text size="4">
                  {getLocalization("professionalAffiliationsContentListItem1")}
                </Text>
              </li>
              <li>
                <Text size="4">
                  {getLocalization("professionalAffiliationsContentListItem2")}
                </Text>
              </li>
              <li>
                <Text size="4">
                  {getLocalization("professionalAffiliationsContentListItem3")}
                </Text>
              </li>
              <li>
                <Text size="4">
                  {getLocalization("professionalAffiliationsContentListItem4")}
                </Text>
              </li>
              <li>
                <Text size="4">
                  {getLocalization("professionalAffiliationsContentListItem5")}
                </Text>
              </li>
              <li>
                <Text size="4">
                  {getLocalization("professionalAffiliationsContentListItem6")}
                </Text>
              </li>
              <li>
                <Text size="4">
                  {getLocalization("professionalAffiliationsContentListItem7")}
                </Text>
              </li>
              <li>
                <Text size="4">
                  {getLocalization("professionalAffiliationsContentListItem8")}
                </Text>
              </li>
            </ul>
          </Flex>
        </Box>
      </Flex>
      <Flex
        width="100%"
        justify="center"
        align="center"
        className={`section ${styles.meetDrCompanioniOuter}`}
      >
        <Flex
          direction="row"
          className={`${styles.meetTheDoctorContainer} ${styles.wideDoctorContainer}`}
        >
          <Flex
            gap="4"
            className={styles.chinchillaImageColumn}
            direction="column"
            align="center"
          >
            <Box p="2">
              <HoverRevealImages
                topImage="/dr-chinchilla.jpg"
                bottomImage="/dr-chinchilla-silly-2026.jpg"
                alt="Dr. Chinchilla"
                className={`${styles.doctorImage} ${styles.drChinchillaPortrait}`}
                fill
              />
            </Box>

            {getLocalization("companioniQuote") ? (
              <Box p="6" className={styles.quoteBoxWithTopArrow}>
                <Text
                  as="p"
                  highContrast={false}
                  size="4"
                  className={styles.quoteText}
                >
                  {getLocalization("companioniQuote")}
                </Text>
              </Box>
            ) : null}
          </Flex>
          <Flex
            gap="4"
            className={`${styles.doctorTextColumn} ${styles.chinchillaTextColumn}`}
            direction="column"
          >
            <Heading as="h2" className={styles.subsectionHeading}>
              {getLocalization("companioniName")}
            </Heading>
            {getLocalization("companioniBackgroundTitle") ? (
              <Heading as="h4" className={styles.heading3}>
                {getLocalization("companioniBackgroundTitle")}
              </Heading>
            ) : null}
            <Text as="p" size="4">
              {getLocalization("companioniBackgroundP1")}
            </Text>
            <Text as="p" size="4">
              {getLocalization("companioniBackgroundP2")}
            </Text>
            {getLocalization("companioniBackgroundP3") ? (
              <Text as="p" size="4">
                {getLocalization("companioniBackgroundP3")}
              </Text>
            ) : null}
            {getLocalization("companioniOutsideTitle") ? (
              <Heading as="h4" className={styles.heading3}>
                {getLocalization("companioniOutsideTitle")}
              </Heading>
            ) : null}
            <Text as="p" size="4">
              {getLocalization("companioniOutsideP1")}
            </Text>
          </Flex>
        </Flex>
      </Flex>
      <Box className={styles.form}>
        <HomeConsultForm />
      </Box>
    </>
  );
}
